/**
 * Download a remote file to the user's device.
 *
 * Firebase Storage download URLs render inline in the browser, and the
 * `<a download>` attribute is ignored for cross-origin URLs — so a plain anchor
 * just opens the image instead of saving it. To force a real download with the
 * correct filename we fetch the bytes and save them through a blob URL. If the
 * fetch is blocked (e.g. Storage CORS not configured for a dev origin), we fall
 * back to opening the file in a new tab so the action never silently fails.
 */
export async function downloadFile(url: string, filename?: string): Promise<void> {
  const name = filename?.trim() || deriveFilename(url);
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    clickDownloadAnchor(objectUrl, name);
    // Give the download a moment to start before releasing the blob.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

function clickDownloadAnchor(href: string, download: string): void {
  const a = document.createElement('a');
  a.href = href;
  a.download = download;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Best-effort filename from a URL when none is supplied. */
function deriveFilename(url: string): string {
  try {
    const path = new URL(url).pathname;
    const last = decodeURIComponent(path.split('/').pop() || '');
    // Firebase stores objects under o/<encoded-path>; keep the final segment.
    return last.split('%2F').pop() || last || 'download';
  } catch {
    return 'download';
  }
}
